import { useEffect, useState, type CSSProperties } from 'react';
import { color } from '../styles/theme';
import { siteDay } from '../lib/format';
import { IconSearch } from '../components/icons';
import { useAuth } from '../auth/AuthContext';
import type { MeetingItem, MeetingPhase, MeetingRow } from '../api/client';
import {
  isEndpointMissing,
  useMeetings,
  useOpenActions,
  useUpdateItem,
} from '../api/meetings';
import { SectionTabs } from '../components/SectionTabs';
import { PreviewPill } from '../components/pills';
import { Button, Card, Chip, ColHead, ErrorNote, Loading, PageTitle } from '../components/ui';
import { AboutPanel, DueTag, Faces, PhasePill, whenLabel } from './MeetingBits';
import { NewMeeting } from './NewMeeting';
import { MeetingPage } from './MeetingPage';

/**
 * Meetings: the list, with what needs attention pinned first.
 *
 * One meeting moves through three phases — Preparing, In meeting, Done — and
 * a new one can continue the last, so the chain of weekly meetings is one
 * series. The list shows the chain; the page (MeetingPage) is where the
 * meeting happens. docs/api-contract-meetings.md has the rules.
 */

type View = 'attention' | MeetingPhase | 'all';

const PAGE_SIZE = 25;
const GRID = 'minmax(0,2.4fr) minmax(150px,1fr) 108px minmax(120px,0.9fr) 128px 96px';
const ACTION_GRID = 'minmax(0,2.2fr) minmax(0,1.4fr) minmax(120px,0.8fr) 120px 110px';
/** Where the open meeting is remembered, so a refresh mid-meeting lands back on it. */
const OPEN_KEY = 'enact.meetings.open';

const remembered = (): string | null => {
  try {
    return sessionStorage.getItem(OPEN_KEY);
  } catch {
    return null;
  }
};

const remember = (id: string | null): void => {
  try {
    if (id) sessionStorage.setItem(OPEN_KEY, id);
    else sessionStorage.removeItem(OPEN_KEY);
  } catch {
    /* a private window: forgotten on refresh */
  }
};

export function Meetings() {
  const { can } = useAuth();
  const canUpdate = can('meetings:update');

  const [view, setView] = useState<View>('all');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(remembered);
  const [creating, setCreating] = useState(false);
  const [mine, setMine] = useState(false);

  useEffect(() => setPage(1), [view, q]);
  useEffect(() => remember(openId), [openId]);

  const phase: MeetingPhase | undefined = view === 'attention' || view === 'all' ? undefined : view;
  const list = useMeetings({ ...(phase ? { phase } : {}), ...(q.trim() ? { q: q.trim() } : {}), page, pageSize: PAGE_SIZE });
  // One probe decides the preview for the whole section: the collection
  // route has no id in it, so its 404 can only mean the routes are missing.
  const preview = isEndpointMissing(list.error);
  const late = useOpenActions({ owner: mine ? 'me' : 'all', state: 'late' }, !preview);
  const lateAll = useOpenActions({ owner: 'all', state: 'late' }, !preview);

  if (openId && !preview) {
    return <MeetingPage id={openId} onBack={() => setOpenId(null)} onOpen={setOpenId} />;
  }

  // An older API without the meeting routes lists nothing — never invented rows.
  const rows: MeetingRow[] = preview ? [] : (list.data?.rows ?? []);
  const attention = lateAll.data?.actions.length ?? 0;

  const tabs: ReadonlyArray<{ key: View; label: string; badge?: number }> = [
    { key: 'attention', label: 'Needs attention', badge: attention },
    { key: 'preparing', label: 'Preparing' },
    { key: 'in_meeting', label: 'In meeting' },
    { key: 'done', label: 'Done' },
    { key: 'all', label: 'All' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }} className="fade-in">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <PageTitle sub="Prepare the agenda, take notes in the room, send the summary. A meeting can continue the last one.">
            Meetings
          </PageTitle>
        </div>
        {preview && <PreviewPill />}
        {canUpdate && (
          <Button
            variant="navy"
            disabled={preview}
            title={preview ? 'This API does not have meetings yet.' : undefined}
            onClick={() => setCreating(true)}
          >
            New meeting
          </Button>
        )}
      </div>

      <AboutPanel storageKey="enact.meetings.about" title="About meetings">
        Everyone in the room adds the agenda items they want to talk about while a meeting is <strong>Preparing</strong>, by
        hand or with Ena, the meeting assistant. <strong>In meeting</strong>, notes, questions, takeaways and action items are typed
        as they happen; an action item is an owner, a due date and done. <strong>Done</strong> is the summary, written or
        drafted by Ena, sent to the attendees. A new meeting can continue the last one: its action items, takeaways
        and open questions come along, to settle or put on the agenda. Refer to a participant by record ID, never by
        name — notes are the whole lab's.
      </AboutPanel>

      <SectionTabs value={view} onChange={setView} tabs={tabs} compact />

      {view === 'attention' ? (
        <AttentionTable
          actions={late.data?.actions ?? []}
          loading={late.isLoading && !late.data}
          error={late.error && !preview ? (late.error as Error).message : null}
          preview={preview}
          mine={mine}
          onMine={setMine}
          canUpdate={canUpdate}
          onOpen={setOpenId}
        />
      ) : (
        <Card pad={0} style={{ overflow: 'hidden' }}>
          <div style={toolbar}>
            <div style={searchBox}>
              <span style={{ color: color.muted, display: 'flex', flex: 'none' }}>
                <IconSearch size={15} />
              </span>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by title"
                aria-label="Search meetings by title"
                style={{ border: 'none', outline: 'none', fontSize: 13, color: color.ink, flex: 1, background: 'transparent', minWidth: 0 }}
              />
            </div>
            <span style={{ fontSize: 13, color: color.muted }}>
              <strong style={{ color: color.ink }}>{preview ? rows.length : (list.data?.total ?? 0)}</strong>{' '}
              {(preview ? rows.length : list.data?.total) === 1 ? 'meeting' : 'meetings'}
            </span>
          </div>

          <div className="x-scroll">
            <div style={{ minWidth: 860 }}>
              <div style={{ ...gridRow, padding: '10px 20px', borderBottom: `1px solid ${color.line}` }}>
                <ColHead>Meeting</ColHead>
                <ColHead>When</ColHead>
                <ColHead>Phase</ColHead>
                <ColHead>In the room</ColHead>
                <ColHead>Action items</ColHead>
                <ColHead>Summary</ColHead>
              </div>

              {list.isLoading && !list.data && !preview ? (
                <Loading />
              ) : list.error && !preview ? (
                <ErrorNote message={(list.error as Error).message} />
              ) : rows.length === 0 ? (
                <p style={{ padding: '28px 20px', fontSize: 14, color: color.muted, margin: 0 }}>
                  {q.trim() || phase ? 'No meeting matches.' : 'No meetings yet. Create the first one and it appears here.'}
                </p>
              ) : (
                rows.map((r) => <Row key={r.id} r={r} onOpen={setOpenId} />)
              )}
            </div>
          </div>

          {!preview && list.data && list.data.pageMax > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', borderTop: `1px solid ${color.line}` }}>
              <span style={{ fontSize: 12.5, color: color.muted, flex: 1 }}>
                Page {list.data.page} of {list.data.pageMax}
              </span>
              <Button variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </Button>
              <Button variant="ghost" disabled={page >= list.data.pageMax} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </div>
          )}
        </Card>
      )}

      {creating && (
        <NewMeeting
          continuesFrom={null}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            setOpenId(id);
          }}
        />
      )}
    </div>
  );
}

const gridRow: CSSProperties = { display: 'grid', gridTemplateColumns: GRID, gap: 12, alignItems: 'center' };

const toolbar: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
  padding: '14px 20px',
  borderBottom: `1px solid ${color.line}`,
};

const searchBox: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  background: color.bg,
  borderRadius: 999,
  padding: '9px 16px',
  width: 'min(260px,50vw)',
};

function Row({ r, onOpen }: { r: MeetingRow; onOpen: (id: string) => void }) {
  return (
    <div className="hov-row" style={{ ...gridRow, padding: '11px 20px', borderBottom: `1px solid ${color.line}` }}>
      <button
        onClick={() => onOpen(r.id)}
        style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', minWidth: 0 }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 700, color: color.ink, minWidth: 0 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</span>
        </span>
        <span style={{ display: 'block', fontSize: 11.5, color: color.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {r.continuesFromTitle ? `Continues ${r.continuesFromTitle}` : 'Started fresh'}
        </span>
      </button>
      <span style={{ fontSize: 12.5, color: r.scheduledAt ? color.ink : color.muted, whiteSpace: 'nowrap' }}>{whenLabel(r.scheduledAt)}</span>
      <span>
        <PhasePill phase={r.phase} />
      </span>
      <Faces people={r.attendees} />
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: color.ink }}>
        {r.counts.actionsOpen === 0 ? (
          <span style={{ color: color.muted }}>{r.phase === 'preparing' ? '—' : 'none open'}</span>
        ) : (
          <span>{r.counts.actionsOpen} open</span>
        )}
        {r.counts.actionsLate > 0 && (
          <span style={{ fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 999, background: '#fdf1ee', color: '#a4432f' }}>
            {r.counts.actionsLate} late
          </span>
        )}
      </span>
      <span style={{ fontSize: 12.5, color: r.sentAt ? color.ink : color.muted, whiteSpace: 'nowrap' }}>
        {r.sentAt ? `Sent ${siteDay(r.sentAt)}` : r.phase === 'done' ? 'Not sent' : '—'}
      </span>
    </div>
  );
}

/** Open action items overdue or due today, across every meeting. */
function AttentionTable({
  actions,
  loading,
  error,
  preview,
  mine,
  onMine,
  canUpdate,
  onOpen,
}: {
  actions: MeetingItem[];
  loading: boolean;
  error: string | null;
  preview: boolean;
  mine: boolean;
  onMine: (v: boolean) => void;
  canUpdate: boolean;
  onOpen: (id: string) => void;
}) {
  const update = useUpdateItem();
  return (
    <Card pad={0} style={{ overflow: 'hidden' }}>
      <div style={toolbar}>
        <span style={{ fontSize: 13, color: color.ink }}>Action items overdue or due today.</span>
        <span style={{ flex: 1 }} />
        <Chip label="Everyone's" active={!mine} onClick={() => onMine(false)} />
        <Chip label="Mine" active={mine} onClick={() => onMine(true)} />
      </div>
      <div className="x-scroll">
        <div style={{ minWidth: 820 }}>
          <div style={{ display: 'grid', gridTemplateColumns: ACTION_GRID, gap: 12, padding: '10px 20px', borderBottom: `1px solid ${color.line}` }}>
            <ColHead>Action item</ColHead>
            <ColHead>Meeting</ColHead>
            <ColHead>Owner</ColHead>
            <ColHead>Due</ColHead>
            <span />
          </div>
          {preview ? (
            <p style={{ padding: '28px 20px', fontSize: 14, color: color.muted, margin: 0 }}>
              Action items appear here once this API has meetings.
            </p>
          ) : loading ? (
            <Loading />
          ) : error ? (
            <ErrorNote message={error} />
          ) : actions.length === 0 ? (
            <p style={{ padding: '28px 20px', fontSize: 14, color: color.muted, margin: 0 }}>
              {mine ? 'Nothing of yours is overdue or due today.' : 'Nothing is overdue or due today.'}
            </p>
          ) : (
            actions.map((a) => (
              <div
                key={a.id}
                className="hov-row"
                style={{ display: 'grid', gridTemplateColumns: ACTION_GRID, gap: 12, alignItems: 'center', padding: '11px 20px', borderBottom: `1px solid ${color.line}`, fontSize: 13.5 }}
              >
                <span style={{ color: color.ink, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{a.body}</span>
                <button
                  onClick={() => onOpen(a.meetingId)}
                  style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontSize: 13, fontWeight: 700, color: color.blue, minWidth: 0 }}
                  className="hov-underline"
                >
                  <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.meetingTitle}</span>
                  <span style={{ display: 'block', fontSize: 11.5, fontWeight: 400, color: color.muted }}>{whenLabel(a.scheduledAt)}</span>
                </button>
                <span style={{ fontSize: 12.5, color: a.ownerName ? color.ink : color.muted }}>{a.ownerName ?? 'Needs an owner'}</span>
                <DueTag dueOn={a.dueOn} status={a.status} />
                <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {canUpdate && (
                    <Button
                      variant="soft"
                      style={{ padding: '5px 11px', fontSize: 12 }}
                      disabled={update.isPending}
                      onClick={() => update.mutate({ id: a.meetingId, itemId: a.id, status: 'done' })}
                    >
                      Mark done
                    </Button>
                  )}
                </span>
              </div>
            ))
          )}
          {update.error && (
            <div style={{ padding: '0 20px 12px' }}>
              <ErrorNote message={(update.error as Error).message} />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
